import { Button } from '@tunasong/ui-lib'
import { Typography } from '@tunasong/ui-lib'
import { Check, Fail } from '@tunasong/icons'
import { getRandomNote, GuitarLib, NoteLib } from '@tunasong/music-lib'
import { Fretboard } from '@tunasong/music-ui'
import type { NoteName, Persisted } from '@tunasong/schemas'
import { standardNoteNames } from '@tunasong/schemas'
import type { FC, JSX } from 'react'
import { useCallback, useRef, useState } from 'react'
import ScorePanel from './score-panel.js'
import { useScorer } from './scorer.hook.js'
import type { FretNoteGame as FretNoteGameType } from '../types.js'
import { useGameResults } from './game-results.hook.js'
import styles from './fret-note-game.module.css'

const pickChallenge = () => {
  const note = getRandomNote()
  const fingerings: GuitarLib.Fingering[] = []
  for (let str = 1; str <= 6; str++) {
    for (let fret = 1; fret < 12; fret++) {
      const f = { str: str as GuitarLib.StringNumber, fret }
      const n = GuitarLib.getNoteFromFingering(f)
      if (n && NoteLib.equals(n, note)) {
        fingerings.push(f)
      }
    }
  }
  const withMeta = GuitarLib.getFingeringWithMetadata(fingerings)
  return { note, fingering: withMeta[Math.floor(Math.random() * withMeta.length)] }
}

export interface FretNoteGameProps {
  /** The game */
  game: Persisted<FretNoteGameType>
}

export const FretNoteGame: FC<FretNoteGameProps> = props => {
  const { game } = props

  const [icons, setIcons] = useState<Partial<Record<NoteName, JSX.Element | undefined>>>({})
  const [feedback, setFeedback] = useState<{ text: string; correct: boolean } | null>(null)

  const [{ note, fingering }, setChallenge] = useState(() => pickChallenge())
  const nextChallenge = () => setChallenge(pickChallenge())

  const handleSaveScore = () => {}

  const userGameResults = useGameResults({ game })

  const { score, userStats, correct, fail } = useScorer({ userGameResults, saveScore: handleSaveScore })

  const timeout = useRef<number | NodeJS.Timeout>(null)
  const clearFeedback = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current)
    }
    timeout.current = setTimeout(() => {
      setIcons({})
      setFeedback(null)
    }, 3000)
  }, [])

  const handleClick = useCallback(
    (n: NoteName) => () => {
      const isCorrect = NoteLib.equals(n, note)
      if (isCorrect) {
        setIcons(icons => ({ ...icons, [n]: <Check color="var(--tuna-palette-success-main)" /> }))
        setFeedback({ text: `Correct! The note was ${note}`, correct: true })
        correct()
      } else {
        setIcons(icons => ({ ...icons, [n]: <Fail color="var(--tuna-palette-error-main)" /> }))
        setFeedback({ text: `Wrong! The note was ${note}, not ${n}`, correct: false })
        fail()
      }
      nextChallenge()
      clearFeedback()
    },
    [clearFeedback, correct, fail, note]
  )

  return (
    <div className={styles.container}>
      <Fretboard fingerings={[fingering]} labels={'none'} />
      {userStats ? <ScorePanel score={score} userStats={userStats} game={game} /> : null}
      {feedback ? (
        <Typography variant="h6" className={feedback.correct ? styles.correct : styles.wrong}>
          {feedback.text}
        </Typography>
      ) : (
        <Typography variant="h6">Click the correct note name</Typography>
      )}
      <div className={styles.noteGrid}>
        {standardNoteNames.map(n => (
          <Button variant="outlined" key={n} onClick={handleClick(n)} endIcon={icons[n]}>
            {n}
          </Button>
        ))}
      </div>
    </div>
  )
}

export default FretNoteGame
